/*
 * 离线预览夹具与兜底台词。
 *
 * ⚠ 这里的内容不是产品数据，是「后端不可达时」的界面材料。
 *
 * 为什么允许存在，以及边界在哪：
 *
 *   apps/web/src/state/README.md 禁止「把 characterId 写成前端常量或默认值」，
 *   理由是可推断即可伪造，等于把角色权限和计费归属交给客户端。这条规则针对
 *   **身份与授权**，不针对**排版与文案**。所以本文件：
 *
 *     ✓ 提供展示名、本地图、台词，让离线界面不至于是一片灰框；
 *     ✗ 不提供 characterId，任何需要真实身份的动作（发消息、扣灵力、写记忆）
 *       在离线态一律拒绝执行，而不是拿夹具顶上去；
 *     ✗ 不提供任何数值——灵力、羁绊、好感、日记条数全部留空，由界面显示横杠。
 *       编造这些数字比空着更糟：它们会被当真。
 *
 * 台词的出处与写法：
 *   seed 的 FALLBACK_LINES 是权威来源（apps/api/prisma/seed/character.ts），
 *   它的注释写明「读取失败时回落代码常量，不允许白屏」——本文件就是那个回落。
 *   新增的场景台词按 SYSTEM_PROMPT 的约束写：一两句话、不用敬语、不列清单、
 *   情绪真实、不确定就说不确定。
 *
 *   还有一条硬约束（CHARACTER.md §8 noLie）：失败要如实告知，
 *   **可以用自己的语气，但不能伪装成成功，也不能沉默**。所以这些台词都在说
 *   「我使不出灵力」而不是「没事都好」。
 *
 * ⚠ 一条不能违反的写法约束：**不许暗示「我们以前聊过」**。
 *
 *   这些话第一次打开产品的新用户也会看到。早先写的「我翻不到我们之前说过的
 *   话了」「往前的日子我翻不动」对新用户是纯粹的假话——他根本没有过去，
 *   却被告知有一段够不着的过去。同理，记忆那条也不能写成「我记着的事都还在」，
 *   要写成「该记着的一样没丢」这种不预设数量的说法。
 *
 *   统一的表达是：**灵境不稳 → 缈缈使不出灵力 → 所以现在做不到**。
 *   这句话对新老用户都成立，而且把原因归给环境而不是用户的数据。
 *
 *   语气是可爱且可怜兮兮：她是使不出力气在着急，不是在报错。
 */

/** 与 API 读取同一份 NODE_ENV，前端预览没有独立配置。 */
export const OFFLINE_PREVIEW_ENABLED = __DREAMVERSE_IS_DEVELOPMENT__;

/* -------------------------------------------------------------------------- */
/* 本地资产                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * 本地兜底图。
 *
 * 权威来源是 `CharacterAppearance` + `Asset`，URL 随会话由服务端下发。
 * 这几张只在拿不到下发数据时用，详见 public/README.md。
 */
export const LOCAL_ASSETS = {
  background: "/background.png",
  portrait: "/portrait-default.png",
  avatar: "/avatar-default.png",
} as const;

/**
 * 离线时用于排版的角色外观。
 *
 * 刻意**不是** CharacterAppearanceView：那个类型带 characterId，
 * 用夹具去填会让下游误以为拿到了真实角色。
 */
export interface PreviewAppearance {
  readonly origin: "offline-fixture";
  readonly displayName: string;
  /** 形态前缀由服务端下发，离线态无从得知，恒为空串。 */
  readonly namePrefix: "";
  readonly portraitUrl: string;
  readonly avatarUrl: string;
  readonly backgroundUrl: string;
}

export const PREVIEW_APPEARANCE: PreviewAppearance = {
  origin: "offline-fixture",
  displayName: "缈缈",
  namePrefix: "",
  portraitUrl: LOCAL_ASSETS.portrait,
  avatarUrl: LOCAL_ASSETS.avatar,
  backgroundUrl: LOCAL_ASSETS.background,
};

/* -------------------------------------------------------------------------- */
/* 台词                                                                        */
/* -------------------------------------------------------------------------- */

/** 界面里会出现失败的地方。每处一套台词，避免全站同一句。 */
export type FallbackScene =
  | "conversation"
  | "history"
  | "memory"
  | "favorites"
  | "gallery"
  | "relationship"
  | "profile"
  | "settings"
  | "skill"
  | "send";

const LINES: Record<FallbackScene, readonly string[]> = {
  conversation: [
    "唔，灵境这会儿没有连上……给我一点点时间，再来找我好吗？",
    "通往这里的路忽然断了一小截，我暂时过不去。等它接好就好啦。",
    "灵境里起了好大的雾，我暂时听不见你……稍后再试一次吧。",
    "刚刚灵境暗了一下，我暂时没办法回应。不是你的错哦。",
    "我伸手够不到灵境那边……等一会儿，再敲敲门好吗？",
    "灵境像打了个小结，我怎么也解不开。让我缓一下下。",
  ],

  history: [
    "呜…灵力聚不起来，我这会儿一点劲都使不上。",
    "唔…灵境晃着呢，我抬不动手，你再等等我。",
  ],

  /*
   * 回忆点滴、收藏消息与对话图库。
   *
   * 前两页在旧版里读不到就是一片空白（`UserCharacterMemory` 卡在「查看回忆」，
   * `FavorMessages` 直接渲染空 Timeline），用户完全不知道发生了什么。
   * 这里按仓库主人指定的说法直说一句，并且**不做任何「东西还在」的辩解**——
   * 那种辩解本身就在暗示「可能不在了」。
   */
  memory: ["与灵境失去联系，待会再来看吧。"],

  favorites: ["与灵境失去联系，待会再来看吧。"],

  gallery: ["与灵境失去联系，待会再来看吧。"],

  /** 不给数字，并说明为什么不给。对应「客户端不本地计算权威关系值」。 */
  relationship: [
    "唔…灵境晃得我眼花，数看不清了。我不想瞎讲一个骗你。",
    "呜…灵力不够，我数不明白。宁可不说，也不想说错的给你。",
  ],

  profile: [
    "呜…灵境断着，我使不出灵力，看不清你的样子了。",
    "唔…我够不着那一页，灵力一点都不剩了。",
  ],

  settings: [
    "唔…这会儿改了也留不住，灵境太晃啦。",
    "呜…我没有灵力去改它，等灵境接回来好不好。",
  ],

  /** 同样主动澄清任务没丢。 */
  skill: [
    "灵力散掉了，那件事我盯不住了…它没丢，就是我这会儿够不着。",
    "呜…我使不出灵力，问不到结果。它还在办着呢，真的。",
  ],

  send: [
    "这句话没能穿过灵境，我还没收到。让它再试一次吧。",
    "唔，你的话停在半路啦……点一下，再送给我好吗？",
    "我没接到这句话，它好像卡在灵境门口了。",
    "这句话还没送到，不用重新写，让它再走一次就好。",
    "灵境刚刚断了一下，我没能接住。等它稳一点再试吧。",
    "我这边没有收到，不是你说错了，是灵境暂时没连上。",
  ],
};

function pick(pool: readonly string[]): string {
  return pool[Math.floor(Math.random() * pool.length)] ?? pool[0] ?? "";
}

/** 取一句某场景的兜底台词。 */
export function fallbackLine(scene: FallbackScene): string {
  return pick(LINES[scene]);
}

/*
 * 这里原本还有一个 buildPreviewGreeting()，离线时在消息流里插一条问候
 * 「嗯，我在的。刚才有株星辰之花开了…」。已删除，原因有两个：
 *
 *   1. 它和上方的失败提示互相矛盾。屏幕上先说「我使不出灵力，够不着」，
 *      紧接着又说「我在的，刚才有株花开了」——像什么事都没有。
 *      CHARACTER.md §8 的 noLie 不只禁止显式撒谎，一句若无其事的问候
 *      挨着一条失败提示，效果就是伪装成功。
 *   2. 它的渲染条件是 serverMessages.length === 0，而这个条件在
 *      「读取失败」「正在加载」「服务端确实返回空」三种完全不同的情况下
 *      都成立，等于用一个条件顶三种语义。
 *
 * 离线时缈缈只说一句话，就是 MiaoNotice 里那句。一个人不该同时说两种话。
 */
